import { useState, useEffect } from "react";
import { PriceUpdate } from "../types/common";

const COINGECKO_API_KEY = "CG-JwxWoYDHBHj9gWm31hEcquiz";
const PRICE_UPDATE_INTERVAL = 60_000; // 1 minute

// Map your “logical” names to CoinGecko exchange_ids
const EXCHANGE_ID_MAP: Record<string, string> = {
  kraken: "kraken",
  "kraken-pro": "kraken",
  ndax: "ndax",
  coinbase: "coinbase",
  "coinbase-pro": "gdax",
  "crypto.com": "crypto-com",
  netcoins: "netcoins",
  virgocx: "virgocx",
  newton: "newton",
  wealthsimple: "wealthsimple",
  "bitcoin-well": "bitcoinwell",
  shakepay: "shakepay",
  coinsquare: "coinsquare",
  chicksx: "chicksx",
  "1bitcoin-express": "1btc-express",
  bitbuy: "bitbuy",
  "bull-bitcoin": "bullbitcoin",
  "mybtc.ca": "mybtc-ca",
  "beaver-bitcoin": "beaver-bitcoin",
};

export function usePriceUpdates() {
  const [priceData, setPriceData] = useState<
    PriceUpdate & {
      exchangePrices: Record<string, number>;
    }
  >({
    price: 0,
    change: 0,
    timestamp: Date.now(),
    exchangePrices: {},
  });

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        // 1) Generic BTC→CAD price + 24h change
        const simpleUrl = new URL(
          "https://api.coingecko.com/api/v3/simple/price"
        );
        simpleUrl.search = new URLSearchParams({
          ids: "bitcoin",
          vs_currencies: "cad",
          include_24hr_change: "true",
          x_cg_demo_api_key: COINGECKO_API_KEY,
        }).toString();

        const simpleRes = await fetch(simpleUrl.toString());
        if (!simpleRes.ok) throw new Error("Simple price fetch failed");
        const simpleJson = await simpleRes.json();
        const genericPrice = Math.round(simpleJson.bitcoin.cad);
        const change = simpleJson.bitcoin.cad_24h_change;

        // 2) Exchange‑specific BTC→CAD prices
        const exchangeIds = Object.values(EXCHANGE_ID_MAP).join(",");
        const tickersUrl = new URL(
          "https://api.coingecko.com/api/v3/coins/bitcoin/tickers"
        );
        tickersUrl.search = new URLSearchParams({
          exchange_ids: exchangeIds,
          vs_currency: "cad",
          x_cg_demo_api_key: COINGECKO_API_KEY,
        }).toString();

        const tickersRes = await fetch(tickersUrl.toString());
        if (!tickersRes.ok) throw new Error("Tickers fetch failed");
        const tickersJson = await tickersRes.json();

        // Build a map: { kraken: 117044.9, coinbase: 117102.3, … }
        const tickerMap: Record<string, number> = {};
        for (const t of tickersJson.tickers) {
          if (t.target === "CAD" && t.market?.identifier) {
            tickerMap[t.market.identifier] = t.last;
          }
        }

        // 3) Fill in exchangePrices with falling back
        const exchangePrices: Record<string, number> = {};
        const deviation = genericPrice * 0.0005;

        for (const [key, cgId] of Object.entries(EXCHANGE_ID_MAP)) {
          const exchangePrice = tickerMap[cgId];
          if (exchangePrice != null) {
            exchangePrices[key] = exchangePrice;
          } else {
            const randomOffset = (Math.random() * 2 - 1) * deviation;
            exchangePrices[key] = Math.round(genericPrice + randomOffset);
          }
        }

        // 4) Update state
        setPriceData({
          price: genericPrice,
          change,
          timestamp: Date.now(),
          exchangePrices,
        });
      } catch (err) {
        console.error(err);
      }
    };

    fetchPrices();
    const iv = setInterval(fetchPrices, PRICE_UPDATE_INTERVAL);
    return () => clearInterval(iv);
  }, []);

  // console.log("BTC CAD Price Data: \n", priceData);
  return priceData;
}
